import type { GameStore } from '../../store/GameStore';
import type { GameEvent } from '../../core/events/GameEvents';

/**
 * Dramatic review score reveal dialog.
 * Shows scores one at a time with the final average and HoF status.
 */
export function openReviewRevealDialog(
  parent: HTMLElement,
  store: GameStore,
  event: GameEvent,
  onClose: () => void,
): void {
  if (event.type !== 'REVIEW_SCORES_GENERATED') return;

  const prevSpeed = store.getState().speed;
  store.dispatch({ type: 'SET_SPEED', speed: 0 });

  const overlay = document.createElement('div');
  overlay.className = 'game-ui';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.8);z-index:300;display:flex;align-items:center;justify-content:center;';

  const card = document.createElement('div');
  card.style.cssText = 'background:var(--color-bg-secondary);border:2px solid var(--border-color);border-radius:8px;padding:32px;max-width:400px;width:90vw;text-align:center;';

  // Title
  const title = document.createElement('div');
  title.textContent = 'REVIEW SCORES';
  title.style.cssText = 'font-size:12px;color:var(--color-text-muted);letter-spacing:2px;margin-bottom:4px;';
  card.appendChild(title);

  const nameEl = document.createElement('div');
  nameEl.textContent = event.name;
  nameEl.style.cssText = 'font-size:20px;font-weight:700;margin-bottom:20px;';
  card.appendChild(nameEl);

  // Score slots
  const scoresContainer = document.createElement('div');
  scoresContainer.style.cssText = 'display:flex;justify-content:center;gap:12px;margin-bottom:20px;';

  const scoreSlots: HTMLDivElement[] = [];
  for (let i = 0; i < 4; i++) {
    const slot = document.createElement('div');
    slot.style.cssText = 'width:60px;height:60px;background:var(--color-bg-primary);border:2px solid var(--border-color);border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:24px;font-weight:700;font-family:var(--font-pixel);color:var(--color-text-muted);';
    slot.textContent = '?';
    scoresContainer.appendChild(slot);
    scoreSlots.push(slot);
  }
  card.appendChild(scoresContainer);

  // Average + HoF (hidden initially)
  const resultEl = document.createElement('div');
  resultEl.style.cssText = 'min-height:60px;';
  card.appendChild(resultEl);

  // Dismiss button (hidden initially)
  const dismissBtn = document.createElement('button');
  dismissBtn.className = 'btn btn--primary';
  dismissBtn.textContent = 'OK';
  dismissBtn.style.cssText = 'padding:10px 32px;font-size:14px;display:none;margin-top:12px;';
  dismissBtn.addEventListener('click', () => {
    overlay.remove();
    store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
    onClose();
  });
  card.appendChild(dismissBtn);

  overlay.appendChild(card);
  parent.appendChild(overlay);

  // Reveal scores one at a time
  const scores = event.scores;
  const reviewerNames = ['GamePro', 'PixelCritic', 'IndieWatch', 'TechGamer'];

  let revealed = 0;
  const revealNext = () => {
    if (revealed >= 4) {
      // Show average
      const avgColor = event.average >= 8 ? 'var(--color-success)' : event.average >= 5 ? 'var(--color-text-primary)' : 'var(--color-danger)';
      resultEl.innerHTML = `
        <div style="font-size:14px;color:var(--color-text-muted);margin-bottom:4px;">Average Score</div>
        <div style="font-size:36px;font-weight:700;font-family:var(--font-pixel);color:${avgColor};">${event.average.toFixed(1)}<span style="font-size:16px;color:var(--color-text-muted);">/10</span></div>
        ${event.isHallOfFame ? '<div style="font-size:18px;color:var(--color-warning);margin-top:8px;">★ HALL OF FAME ★</div>' : ''}
      `;
      dismissBtn.style.display = '';
      return;
    }

    const score = scores[revealed]!;
    const slot = scoreSlots[revealed]!;
    const color = score >= 8 ? 'var(--color-success)' : score >= 5 ? 'var(--color-text-primary)' : 'var(--color-danger)';

    slot.style.borderColor = color;
    slot.style.color = color;
    slot.textContent = score.toFixed(1);
    slot.title = reviewerNames[revealed] ?? '';

    revealed++;
    setTimeout(revealNext, 600);
  };

  setTimeout(revealNext, 500);
}
